import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import './ResetPassword.css';
import {USER_ENDPOINTS} from "../../Constants";

function ResetPassword() {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [isSuccess, setIsSuccess] = useState(false);
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [tokenValidationMessage, setTokenValidationMessage] = useState("");
    const [validating, setValidating] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const buttonRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Extract token from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');

    // Validate token when component mounts
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setTokenValidationMessage("Link-ul de resetare a parolei nu este valid. Lipsește tokenul.");
                setValidating(false);
                return;
            }

            try {
                const response = await axios.get(`${USER_ENDPOINTS.VALIDATE_RESET_TOKEN}?token=${token}`);

                if (response.data.status === 'success') {
                    setIsTokenValid(true);
                } else {
                    setTokenValidationMessage(response.data.message || "Link-ul de resetare a parolei a expirat sau nu este valid.");
                }
            } catch (error) {
                setTokenValidationMessage(
                    error.response?.data?.message ||
                    "Nu am putut valida link-ul de resetare a parolei. Te rugăm să încerci din nou."
                );
            } finally {
                setValidating(false);
            }
        };

        validateToken();
    }, [token]);

    // Handle ripple effect on button click
    const createRipple = (event) => {
        const button = event.currentTarget;
        const circle = document.createElement("span");
        const diameter = Math.max(button.clientWidth, button.clientHeight);
        const radius = diameter / 2;

        // Position the ripple exactly where clicked
        const rect = button.getBoundingClientRect();
        circle.style.width = circle.style.height = `${diameter}px`;
        circle.style.left = `${event.clientX - rect.left - radius}px`;
        circle.style.top = `${event.clientY - rect.top - radius}px`;
        circle.classList.add("ripple");

        // Remove existing ripples
        const ripple = button.getElementsByClassName("ripple")[0];
        if (ripple) {
            ripple.remove();
        }

        button.appendChild(circle);
    };

    const validatePassword = (password) => {
        // Password must be at least 6 characters long and include at least one letter and one number
        const regex = /^(?=.*[A-Za-z])(?=.*\d).{6,}$/;
        return regex.test(password);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate passwords
        if (!password || !confirmPassword) {
            setMessage("Te rugăm să completezi ambele câmpuri.");
            return;
        }

        if (!validatePassword(password)) {
            setMessage("Parola trebuie să aibă cel puțin 6 caractere și să conțină cel puțin o literă și o cifră.");
            return;
        }

        if (password !== confirmPassword) {
            setMessage("Parolele nu se potrivesc.");
            return;
        }

        try {
            setIsLoading(true);
            setMessage("");

            const response = await axios.post(USER_ENDPOINTS.RESET_PASSWORD, {
                token,
                password,
                confirmPassword
            });

            setIsSuccess(true);
            setMessage(response.data.message || "Parola a fost resetată cu succes.");

            // Redirect to login page after 3 seconds
            setTimeout(() => {
                navigate("/login");
            }, 3000);

        } catch (error) {
            setIsSuccess(false);
            setMessage(
                error.response?.data?.message ||
                "A apărut o eroare. Te rugăm să încerci din nou."
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Show loading spinner while validating token
    if (validating) {
        return (
            <div className="reset-password-container">
                <div className="token-validation-box">
                    <div className="loading-spinner"></div>
                    <p>Se validează link-ul de resetare...</p>
                </div>
            </div>
        );
    }

    // Show error message if token is invalid
    if (!isTokenValid && !validating) {
        return (
            <div className="reset-password-container">
                <div className="token-validation-box invalid">
                    <div className="invalid-icon">!</div>
                    <h2>Link invalid</h2>
                    <p>{tokenValidationMessage}</p>
                    <Link to="/forgot-password" className="request-new-link">
                        Solicită un nou link
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-password-container">
            <div className="reset-password-image">
                <div className="reset-password-image-overlay">
                    <h1>Resetare parolă</h1>
                    <p>Alegeți o parolă nouă și sigură pentru contul dumneavoastră.</p>
                </div>
            </div>
            <div className="reset-password-box">
                <div className="reset-password-header">
                    <h2>Setează o parolă nouă</h2>
                    <p>Parola trebuie să aibă cel puțin 6 caractere și să conțină cel puțin o literă și o cifră.</p>
                </div>
                {isSuccess ? (
                    <div className="success-message">
                        <div className="success-icon">✓</div>
                        <p>{message}</p>
                        <p className="redirect-message">Vei fi redirecționat către pagina de autentificare...</p>
                    </div>
                ) : (
                    <form className="reset-password-form" onSubmit={handleSubmit}>
                        <div className={`input-container ${passwordFocused ? 'focus' : ''}`}>
                            <label htmlFor="password">Parolă nouă</label>
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Introduceți parola nouă"
                                onFocus={() => setPasswordFocused(true)}
                                onBlur={() => setPasswordFocused(false)}
                                required
                            />
                            <FiLock className="input-icon left-icon" />
                            <div
                                className="toggle-password"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <FiEyeOff /> : <FiEye />}
                            </div>
                        </div>
                        <div className={`input-container ${confirmPasswordFocused ? 'focus' : ''}`}>
                            <label htmlFor="confirmPassword">Confirmă parola</label>
                            <input
                                id="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirmați parola nouă"
                                onFocus={() => setConfirmPasswordFocused(true)}
                                onBlur={() => setConfirmPasswordFocused(false)}
                                required
                            />
                            <FiLock className="input-icon left-icon" />
                            <div
                                className="toggle-password"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                            </div>
                        </div>
                        <button
                            type="submit"
                            className={`reset-btn ${isLoading ? 'loading' : ''}`}
                            onClick={createRipple}
                            ref={buttonRef}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner"></span>
                                    <span className="btn-text">Se procesează...</span>
                                </>
                            ) : (
                                <span className="btn-text">Resetează parola</span>
                            )}
                        </button>
                        {message && !isSuccess && <p className="error-message">{message}</p>}
                        <div className="back-link">
                            <Link to="/login">Înapoi la pagina de autentificare</Link>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default ResetPassword;